import { createRef, Component } from 'react'
import Overlay from 'ol/Overlay'
import Select from 'ol/interaction/Select'
import Collection from 'ol/Collection'
import GeoJSON from 'ol/format/GeoJSON'
import * as condition from 'ol/events/condition'
import EXIF from '../../lib/exif'
import PropTypes from 'prop-types'

export default class FeatureSelect extends Component {
  constructor(props) {
    super(props)

    this.popupRef = createRef()
    this.popupContentRef = createRef()
  }

  componentDidMount() {
    this.popupOverlay = new Overlay({
      element: this.popupRef.current,
      autoPan: true,
      autoPanAnimation: { duration: 250 },
    })

    this.props.map.addOverlay(this.popupOverlay)

    this.updateSelect()
  }

  componentWillUnmount() {
    this.props.map.removeInteraction(this.select)
    this.props.map.removeOverlay(this.popupOverlay)
  }

  componentDidUpdate(oldProps) {
    if (
      oldProps.selection !== this.props.selection ||
      oldProps.layers !== this.props.layers
    ) {
      this.updateSelect()
    }
  }

  render() {
    return (
      <span>
        <div className="ol-popup" ref={this.popupRef}>
          <div ref={this.popupContentRef}></div>
        </div>
      </span>
    )
  }

  updateSelect() {
    const idProperty = this.props.featureIdProperty || 'filename'
    const findFeature = (layerId, id) => {
      const findInLayer = layer => {
        const features = layer.getSource().getFeatures()
        return features.find(
          f => f.get('layerId') === layerId && f.get(idProperty) === id
        )
      }

      return Array.from(this.props.layers).reduce(
        (a, l) => a || findInLayer(l),
        null
      )
    }

    this.popupOverlay.setPosition(null)

    const selection = (
      (this.props.selection && this.props.selection.features) ||
      []
    )
      .map(f => findFeature(f.properties.layerId, f.properties[idProperty]))
      .filter(f => f)

    if (this.select) {
      this.props.map.removeInteraction(this.select)
    }

    this.select = new Select({
      layers: layer => this.props.layers.has(layer),
      condition: event => {
        if (this.props.preventDeselect) {
          if (event.type === 'click') {
            return this.props.map.forEachFeatureAtPixel(event.pixel, () => true)
          }
          return false
        }
        return condition.click(event)
      },
      multi: true,
      style: this.props.style,
      features: new Collection(selection),
      hitTolerance: this.props.hitTolerance,
    })

    this.select.on('select', e => {
      if (this.props.disabled) {
        this.updateSelect()
        return
      }

      const features = e.selected
      const featuresGeoJson = new GeoJSON().writeFeaturesObject(features, {
        dataProjection: 'EPSG:4326',
        featureProjection: 'EPSG:3857',
      })

      if (features.length === 0) {
        this.popupOverlay.setPosition(null)
        this.props.onSelect(featuresGeoJson)
        return
      }

      if (this.props.showThumbnails) {
        this.popupContentRef.current.innerHTML = ''
        this.popupOverlay.setPosition(
          features[0].getGeometry().getCoordinates()
        )

        const feature = featuresGeoJson.features[0]
        const imageUrl = this.props.getImageUrl(feature)
        const headers = new Headers()
        headers.append('Range', 'bytes=0-' + 64 * 1024)

        window
          .fetch(imageUrl, { headers })
          .then(response => {
            if (response.status === 200 || response.status === 206) {
              return response.arrayBuffer()
            } else if (response.status === 410) {
              // Image has been archived -- response from Rails backend
              return null
            } else if (response.status === 404) {
              // Image has been archived -- response from R2 worker
              return null
            } else if (response.status === 403) {
              // Image has been archived -- direct S3 response
              return response.text().then(text => {
                const dom = new window.DOMParser().parseFromString(
                  text,
                  'application/xml'
                )
                const code = dom.getElementsByTagName('Code')
                if (
                  code.length === 1 &&
                  code[0].textContent.trim() === 'InvalidObjectState'
                ) {
                  return null
                } else {
                  throw new Error(
                    `Unexpected HTTP response ${response.status}: ${text}}`
                  )
                }
              })
            } else {
              throw new Error(`Unexpected HTTP response ${response.status}.`)
            }
          })
          .then(arrayBuffer => {
            if (arrayBuffer) {
              const exif = EXIF.readFromBinaryFile(arrayBuffer)
              const image = new Image()
              image.src = URL.createObjectURL(exif.thumbnail.blob)
              image.addEventListener('click', () =>
                this.props.onSelect(featuresGeoJson)
              )
              this.popupContentRef.current.appendChild(image)
            } else {
              const info = document.createElement('div')
              info.className = 'tw-mx-3 tw-my-8 tw-text-center'
              info.innerText = 'The image has been archived'
              this.popupContentRef.current.appendChild(info)
            }
          })
          .catch(err => {
            console.log('Failed to fetch thumbnail:', err)
            // Fall back to just selecting the image instead of displaying thumbnail
            this.popupOverlay.setPosition(null)
            this.props.onSelect(featuresGeoJson)
          })
      } else {
        this.props.onSelect(featuresGeoJson)
      }
    })

    this.props.map.addInteraction(this.select)
  }
}

FeatureSelect.propTypes = {
  preventDeselect: PropTypes.bool,
  getImageUrl: PropTypes.func,
  hitTolerance: PropTypes.number,
  featureIdProperty: PropTypes.string,
  layers: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  map: PropTypes.object,
  onSelect: PropTypes.func,
  selection: PropTypes.shape({
    features: PropTypes.arrayOf(PropTypes.object).isRequired,
  }),
  showThumbnails: PropTypes.bool,
  style: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  disabled: PropTypes.bool,
}
